import React, { useEffect, useRef, useState } from 'react';
import styled from 'styled-components';
import DeleteIcon from '@material-ui/icons/DeleteOutline';
import AddIcon from '@material-ui/icons/Add';
import { rebuildMatrix } from '../../matrixItems';
import {
  columnWidthsOf,
  evenSizes,
  roundSizes,
  rowHeightsOf,
  totalOf,
  withSize,
} from '../../matrixGrid';
import { SIGNATURE, formFontPt } from '../thresholdConstants';
import CoordInput from '../../../../molecules/CoordInput';
import { DEFAULT_MAX_LINES } from '../../docOvelayTool';
import { itemName } from './elements';
import { RESULT_OUTCOMES, requiredWhenLabel, requiredWhenOf } from '../../fillLayout';
import {
  STATIC_FIELD_GROUPS,
  STATIC_FIELD_OPTIONS,
  StaticFieldOption,
  formFieldGroupLabel,
  formFieldLabel,
  isFormFieldMapping,
  isGlobalVariableMapping,
  isSameMapping,
  staticFieldGroupOf,
  staticFieldOption,
} from './staticFieldOptions';
import MappingSearchResults, { MappingResultGroup, flattenResults } from './MappingSearchResults';
import { useGetFormFieldMappingsQuery } from '../../../../redux/rtkQuery/apiFormbuilderSlice';
import { dispatch } from '../../../../redux/store';
import { setSaveIsDisabled } from '../../../../redux/slices/thresholdFormSlice';

interface Props {
  data: Record<string, any>;
  setData: (d: Record<string, any>) => void;
  tab: string;
  fieldKey: string;
  selected: boolean;
  onSelect: (k: string | null) => void;
}

// Explains, in the builder, what a "Field mapping" actually does — the select
// on its own read as jargon. Falls back to a warning for a hand-typed value,
// which only prints if the inspection really carries a property by that name.
const staticFieldHint = (fieldName?: string, isDeleted = false) => {
  // A `form:` mapping the server no longer offers. It still resolves -- to
  // nothing -- so the report prints a blank here without any error.
  if (isDeleted) {
    return (
      <DangerHint role="alert">
        {isGlobalVariableMapping(fieldName) ? (
          <>
            <strong>{formFieldLabel(fieldName)}</strong>
            {' was deleted, so this field prints blank. Pick another mapping, or add a '}
            {'variable with the same name again in Settings › Global Variables to reconnect it.'}
          </>
        ) : (
          <>
            <strong>{formFieldLabel(fieldName)}</strong>
            {' is no longer offered by Regular Forms, so this field prints blank. Pick another mapping.'}
          </>
        )}
      </DangerHint>
    );
  }

  if (!fieldName) {
    return (
      <Hint>
        Choose the inspection value this field prints. Nobody types into a
        static field — it is filled in automatically when the report is opened.
      </Hint>
    );
  }

  if (isFormFieldMapping(fieldName)) {
    return (
      <Hint>
        <strong>{formFieldLabel(fieldName)}</strong>
        {' — the same field Regular Forms print, filled in automatically when the report is opened.'}
      </Hint>
    );
  }

  const known = staticFieldOption(fieldName);
  if (!known) {
    return (
      <Hint>
        <strong>{fieldName}</strong> is not one of the listed mappings. It only
        prints if the inspection carries a property with exactly that name.
      </Hint>
    );
  }

  return (
    <Hint>
      <strong>{`${staticFieldGroupOf(fieldName)} → ${known.label}`}</strong>
      {known.hint ? ` — ${known.hint}` : ', filled in automatically when the report is opened.'}
    </Hint>
  );
};

// Editor for a single field. Used both for the selected field and, in
// "Show all" mode, for every field on the page.
type RequiredMode = 'optional' | 'always' | 'conditional';

const REQUIRED_MODES: Array<{ mode: RequiredMode; label: string }> = [
  { mode: 'optional', label: 'No' },
  { mode: 'always', label: 'Always' },
  { mode: 'conditional', label: 'By result' },
];

const DEFAULT_REQUIRED_WHEN = ['partialpass', 'failed'];

const FieldEditorCard = ({ data, setData, tab, fieldKey, selected, onSelect }: Props) => {
  const page = data[tab] || {};
  const field = page[fieldKey];

  // The editable NAME is the label, not the key. Older fields have no label
  // yet, so the key doubles as one until the first rename writes one.
  const [rename, setRename] = useState(field?.label ?? fieldKey);
  const [renameErr, setRenameErr] = useState('');
  useEffect(() => {
    setRename(page[fieldKey]?.label ?? fieldKey);
    setRenameErr('');
  }, [fieldKey]);

  // The Regular Forms field list. One request for the whole session, however
  // many cards are open ("Show all" mounts one per field).
  const { data: mappingData, isSuccess: mappingsLoaded } = useGetFormFieldMappingsQuery(undefined, {
    skip: !field?.isStatic,
  });
  // ~500 Regular Forms fields is too many to scroll, so the select is filtered.
  const [mappingFilter, setMappingFilter] = useState('');
  // The matches popup shows only while the search box has focus, so clicking
  // anywhere else closes it without clearing what was typed.
  const [searchFocused, setSearchFocused] = useState(false);
  const [activeMatch, setActiveMatch] = useState(-1);
  const searchRef = useRef<HTMLInputElement>(null);
  useEffect(() => setActiveMatch(-1), [mappingFilter]);

  if (!field) return null;

  const regularGroups = mappingData?.groups || [];
  const needle = mappingFilter.trim().toLowerCase();
  // The current mapping always survives the filter: a native select whose
  // value has no matching option silently shows the first option instead.
  const matchesNeedle = (groupLabel: string, o: StaticFieldOption) =>
    `${groupLabel} ${o.label}`.toLowerCase().includes(needle);
  const keepOption = (groupLabel: string, o: StaticFieldOption) =>
    !needle || o.value === field.fieldName || matchesNeedle(groupLabel, o);
  const curatedShown = STATIC_FIELD_GROUPS.map(g => ({
    label: g.group,
    options: g.options.filter(o => keepOption(g.group, o)),
  })).filter(g => g.options.length > 0);
  const regularShown = regularGroups
    .map(g => {
      const label = `Regular Forms › ${formFieldGroupLabel(g.path)}`;
      return { label, options: g.fields.filter(o => keepOption(label, o)) };
    })
    .filter(g => g.options.length > 0);
  // The kept selection is not a match — without this the "No fields match"
  // note never showed once a mapping had been chosen.
  const matchCount = [...curatedShown, ...regularShown].reduce(
    (n, g) => n + g.options.filter(o => o.value !== field.fieldName).length,
    0,
  );
  // While searching, the matches pop up under the search box. A closed
  // dropdown looks identical before and after typing, so the filter read as
  // doing nothing until the dropdown was opened by hand. Only true matches --
  // the kept selection is in the select already.
  const matchGroups: MappingResultGroup[] = needle
    ? [...curatedShown, ...regularShown]
      .map(g => ({ label: g.label, options: g.options.filter(o => matchesNeedle(g.label, o)) }))
      .filter(g => g.options.length > 0)
    : [];
  const matchList = flattenResults(matchGroups);
  const showMatches = searchFocused && matchList.length > 0;
  const pickMatch = (value: string) => {
    patchField({ fieldName: value });
    setMappingFilter('');
    searchRef.current?.blur();
  };
  const onSearchKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (!matchList.length) return;
    if (e.key === 'ArrowDown' || e.key === 'ArrowUp') {
      e.preventDefault();
      const step = e.key === 'ArrowDown' ? 1 : -1;
      setActiveMatch(i => (i + step + matchList.length) % matchList.length);
    } else if (e.key === 'Enter' && activeMatch >= 0) {
      e.preventDefault();
      pickMatch(matchList[activeMatch].value);
    } else if (e.key === 'Escape') {
      setMappingFilter('');
    }
  };
  const mappingIsListed =
    !field.fieldName ||
    STATIC_FIELD_OPTIONS.some(o => o.value === field.fieldName) ||
    regularGroups.some(g => g.fields.some(o => o.value === field.fieldName));
  // Offered under a different spelling (a Global Variable re-created as
  // "qa permit fee"): the server still prints it, so it is not deleted.
  const mappingIsOffered =
    mappingIsListed ||
    regularGroups.some(g => g.fields.some(o => isSameMapping(o.value, field.fieldName)));
  // Only once the list has actually loaded: before that every `form:` mapping
  // is unlisted, and flagging them all as deleted would be a false alarm.
  const mappingIsDeleted =
    mappingsLoaded && !mappingIsOffered && isFormFieldMapping(field.fieldName);

  const isSignature = fieldKey === SIGNATURE;
  const isCheckbox = !!field.inputType && Array.isArray(field.items);
  const isMatrix = !!field.matrix;
  const isPlain = !isSignature && !isCheckbox && !isMatrix;

  let kindLabel = 'Field';
  if (isSignature) kindLabel = 'Signature';
  else if (isCheckbox) kindLabel = 'Checkbox group';
  else if (isMatrix) kindLabel = 'Table';

  const commit = (next: Record<string, any>) => {
    setData(next);
    dispatch(setSaveIsDisabled(false));
  };

  // What this field prints at unless it is given a size of its own.
  const globalFontPt = formFontPt(data);

  const patchField = (patch: Record<string, any>) => {
    commit({ ...data, [tab]: { ...page, [fieldKey]: { ...field, ...patch } } });
  };

  const patchMatrix = (patch: Record<string, any>) => {
    const merged = { ...field, ...patch };
    commit({
      ...data,
      [tab]: { ...page, [fieldKey]: { ...merged, matrix: rebuildMatrix(merged) } },
    });
  };

  // Column widths are the ones that genuinely differ on a scanned form, so they
  // get an input each. Rows are almost always uniform, so they get one height
  // that applies to all of them — an individual row can still be dragged on the
  // canvas when a form does something unusual.
  const setColumnWidth = (index: number, raw: string) => {
    const mm = Number(raw);
    if (!Number.isFinite(mm)) return;
    patchMatrix({ columnWidths: roundSizes(withSize(columnWidthsOf(field), index, mm)) });
  };

  const distributeColumns = () => {
    const widths = columnWidthsOf(field);
    patchMatrix({ columnWidths: roundSizes(evenSizes(widths.length, totalOf(widths))) });
  };

  const setRowHeight = (raw: string) => {
    const mm = Number(raw);
    if (!Number.isFinite(mm) || mm <= 0) return;
    const heights = rowHeightsOf(field);
    patchMatrix({
      rowHeights: roundSizes(heights.map(() => mm)),
      gapVertical: `${mm}`,
    });
  };

  const patchItem = (i: number, patch: Record<string, any>) => {
    patchField({
      items: field.items.map((box: any, idx: number) => (idx === i ? { ...box, ...patch } : box)),
    });
  };

  const addItem = () => {
    patchField({
      items: [
        ...field.items,
        // Unticked — see FieldToolbar: a seeded 'x' made the builder preview
        // look pre-answered when the inspector always starts from blank.
        {
          left: '30',
          top: '30',
          text: ' ',
          caption: 'LABEL',
          name: itemName(fieldKey, field.items.length),
        },
      ],
    });
  };

  const deleteItem = (i: number) => {
    patchField({ items: field.items.filter((_: any, idx: number) => idx !== i) });
  };

  // X / Y are the same A4 millimetres the canvas drag writes (origin top-left,
  // Y on the text baseline). Typed values stay raw strings rather than being
  // rounded per keystroke, so a half-typed "1" on the way to "12.5" is not
  // clobbered; every consumer reads them through Number().
  const coordRow = (
    x: any,
    y: any,
    onX: (v: string) => void,
    onY: (v: string) => void,
  ) => (
    <Two>
      <CoordInput axis="x" label="X (mm)" value={x} onChange={onX} />
      <CoordInput axis="y" label="Y (mm)" value={y} onChange={onY} />
    </Two>
  );

  const deleteField = () => {
    const next = { ...page };
    delete next[fieldKey];
    commit({ ...data, [tab]: next });
    if (selected) onSelect(null);
  };

  // Required is one of three modes, stored as `required` (always) or
  // `requiredWhen` (the outcomes that make it required — fillLayout
  // isRequiredField). Never both, so the stored shape cannot contradict itself.
  const requiredWhen = requiredWhenOf(field);
  let requiredMode: RequiredMode = 'optional';
  if (field.required) requiredMode = 'always';
  else if (requiredWhen.length) requiredMode = 'conditional';

  const setRequiredMode = (mode: RequiredMode) => {
    if (mode === requiredMode) return;
    patchField({
      required: mode === 'always',
      // The usual case — notes owed on a Partial Pass or a Fail — is one click.
      requiredWhen: mode === 'conditional' ? DEFAULT_REQUIRED_WHEN : undefined,
    });
  };

  const toggleOutcome = (key: string) => {
    const next = requiredWhen.includes(key)
      ? requiredWhen.filter(k => k !== key)
      : RESULT_OUTCOMES.map(o => o.key).filter(k => k === key || requiredWhen.includes(k));
    if (next.length) patchField({ requiredWhen: next });
  };

  // Renaming edits the LABEL and never the key.
  //
  // It used to rewrite the key, which is the field's identity — so every answer
  // an inspector had already saved under the old key was orphaned the moment an
  // admin corrected a typo. Two fields may now share a name; they stay distinct
  // because their keys do.
  const saveRename = () => {
    const name = rename.trim();
    if (!name || name === (field.label ?? fieldKey)) return;
    setRenameErr('');
    patchField({ label: name });
  };

  return (
    <Card $selected={selected} onMouseDown={() => onSelect(fieldKey)}>
      <Header>
        <HeaderLeft>
          <KindBadge>{kindLabel}</KindBadge>
          {isSignature && <CardTitle>Signature</CardTitle>}
        </HeaderLeft>
        <IconBtn type="button" onClick={deleteField} aria-label="Delete field" title="Delete field">
          <DeleteIcon style={{ fontSize: 18 }} />
        </IconBtn>
      </Header>

      {!isSignature && (
        <Field>
          <Lbl>Name</Lbl>
          <RenameRow>
            <Input value={rename} onChange={e => setRename(e.target.value)} onBlur={saveRename} />
          </RenameRow>
          {renameErr && <Err>{renameErr}</Err>}
        </Field>
      )}

      {/* Stored on the field, so the template rebase (docOvelayTool
          rebaseAnswers spreads template fields) carries it to inspections that
          already exist. Static fields fill themselves and never need it. */}
      {!isSignature && !field.isStatic && (
        <Field>
          <Lbl id={`${fieldKey}-required`}>Required</Lbl>
          <Segmented role="radiogroup" aria-labelledby={`${fieldKey}-required`}>
            {REQUIRED_MODES.map(m => (
              <SegmentBtn
                key={m.mode}
                type="button"
                role="radio"
                aria-checked={requiredMode === m.mode}
                $active={requiredMode === m.mode}
                onClick={() => setRequiredMode(m.mode)}
              >
                {m.label}
              </SegmentBtn>
            ))}
          </Segmented>
          {requiredMode === 'conditional' && (
            <OutcomeRow role="group" aria-label="Required when the result is">
              {RESULT_OUTCOMES.map(o => {
                const on = requiredWhen.includes(o.key);
                return (
                  <OutcomeChip
                    key={o.key}
                    type="button"
                    aria-pressed={on}
                    $on={on}
                    // At least one outcome stays picked; to drop them all,
                    // choose No.
                    disabled={on && requiredWhen.length === 1}
                    onClick={() => toggleOutcome(o.key)}
                  >
                    {o.label}
                  </OutcomeChip>
                );
              })}
            </OutcomeRow>
          )}
          <Hint>
            {requiredMode === 'always' &&
              'The inspector cannot create the report while this field is blank.'}
            {requiredMode === 'conditional' &&
              `Required when the inspection's Activity Status is ${requiredWhenLabel(field)}. Optional for any other result.`}
            {requiredMode === 'optional' && 'The inspector may leave this field blank.'}
          </Hint>
        </Field>
      )}

      {/* Plain / static field */}
      {isPlain && (
        <>
          <Field>
            <Lbl>Text</Lbl>
            <Input
              value={field.text || ''}
              maxLength={field.max ? Number(field.max) : undefined}
              onChange={e => patchField({ text: e.target.value })}
            />
          </Field>
          <Field>
            <Lbl>Text size (pt)</Lbl>
            <Input
              value={field.fontSize || ''}
              placeholder={`${globalFontPt} (form)`}
              onChange={e => patchField({ fontSize: e.target.value })}
            />
          </Field>
          {coordRow(
            field.left,
            field.top,
            v => patchField({ left: v }),
            v => patchField({ top: v }),
          )}
          <Field>
            <Lbl>Max length</Lbl>
            <Input value={field.max || ''} onChange={e => patchField({ max: e.target.value })} />
          </Field>
          <CheckRow>
            <input
              type="checkbox"
              checked={!!field.multiline}
              onChange={e => patchField({ multiline: e.target.checked })}
            />
            <span>Wrap long text onto multiple lines</span>
          </CheckRow>
          {field.multiline ? (
            <>
              <Field>
                <Lbl>Max lines</Lbl>
                <Input
                  value={field.maxLines || ''}
                  placeholder={String(DEFAULT_MAX_LINES)}
                  onChange={e => patchField({ maxLines: e.target.value })}
                />
              </Field>
              <Hint>
                Wraps inside this field&apos;s column, then shrinks once it needs more than Max
                lines. Only switch this on where the form has blank space below the field — extra
                lines flow downward and a 12&nbsp;pt line is about 4.9&nbsp;mm tall.
              </Hint>
            </>
          ) : (
            <Hint>Long text stays on one line and shrinks to fit.</Hint>
          )}
          <CheckRow>
            <input
              type="checkbox"
              checked={!!field.isStatic}
              onChange={e => patchField({ isStatic: e.target.checked })}
            />
            <span>Static (auto-filled) field</span>
          </CheckRow>
          {field.isStatic && (
            <Field>
              <Lbl>Field mapping</Lbl>
              <Input
                ref={searchRef}
                type="search"
                value={mappingFilter}
                placeholder="Search fields, e.g. inspection code"
                aria-expanded={showMatches}
                onChange={e => setMappingFilter(e.target.value)}
                onFocus={() => setSearchFocused(true)}
                onBlur={() => setSearchFocused(false)}
                onKeyDown={onSearchKeyDown}
              />
              {showMatches && (
                <MappingSearchResults
                  anchor={searchRef.current}
                  groups={matchGroups}
                  selectedValue={field.fieldName}
                  activeIndex={activeMatch}
                  onPick={pickMatch}
                />
              )}
              <Select
                value={field.fieldName || ''}
                $invalid={mappingIsDeleted}
                onChange={e => patchField({ fieldName: e.target.value })}
              >
                <option value="">— Select a value —</option>
                {/* Grouped by the record the value is read off, so it is clear
                    which part of the inspection a mapping pulls from. The
                    builder's own shortcuts first, then every Regular Forms
                    field. */}
                {[...curatedShown, ...regularShown].map(g => (
                  <optgroup key={g.label} label={g.label}>
                    {g.options.map(o => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </optgroup>
                ))}
                {/* A Regular Forms mapping shows by name even before the list
                    loads, or after its field is dropped from form_fields.py. */}
                {!mappingIsListed && (
                  <optgroup label={isFormFieldMapping(field.fieldName) ? 'Regular Forms' : 'Custom'}>
                    <option value={field.fieldName}>
                      {isFormFieldMapping(field.fieldName)
                        ? `${formFieldLabel(field.fieldName)}${mappingIsDeleted ? ' (deleted)' : ''}`
                        : field.fieldName}
                    </option>
                  </optgroup>
                )}
              </Select>
              {needle && matchCount === 0 && (
                <Hint>{`No fields match "${mappingFilter.trim()}".`}</Hint>
              )}
              {needle && matchCount > 0 && (
                <Hint>
                  {`${matchCount} matching field${matchCount === 1 ? '' : 's'} — click one, or use ↑ ↓ and Enter.`}
                </Hint>
              )}
              {staticFieldHint(field.fieldName, mappingIsDeleted)}
            </Field>
          )}
        </>
      )}

      {/* Signature */}
      {isSignature && (
        <>
          {coordRow(
            field.left,
            field.top,
            v => patchField({ left: v }),
            v => patchField({ top: v }),
          )}
          <Hint>
            Drag to move; drag a corner to resize the stamp box. The signature is anchored at its
            top-left, not on a text baseline.
          </Hint>
        </>
      )}

      {/* Checkbox / radio group */}
      {isCheckbox && (
        <>
          <CheckRow>
            <input
              type="checkbox"
              checked={field.inputType === 'radio'}
              onChange={e => patchField({ inputType: e.target.checked ? 'radio' : 'checkbox' })}
            />
            <span>Radio buttons (single choice)</span>
          </CheckRow>
          {field.items.map((box: any, i: number) => (
            // eslint-disable-next-line react/no-array-index-key
            <ItemCard key={i}>
              <ItemHead>
                <span>Box {i + 1}</span>
                <IconBtn type="button" onClick={() => deleteItem(i)} aria-label="Delete box">
                  <DeleteIcon style={{ fontSize: 16 }} />
                </IconBtn>
              </ItemHead>
              <Field>
                <Lbl>Caption</Lbl>
                <Input value={box.caption || ''} onChange={e => patchItem(i, { caption: e.target.value })} />
              </Field>
              <Field>
                <Lbl>Text size (pt)</Lbl>
                <Input
                  value={box.fontSize || ''}
                  placeholder={`${globalFontPt} (form)`}
                  onChange={e => patchItem(i, { fontSize: e.target.value })}
                />
              </Field>
              {coordRow(
                box.left,
                box.top,
                v => patchItem(i, { left: v }),
                v => patchItem(i, { top: v }),
              )}
            </ItemCard>
          ))}
          <OutlineBtn type="button" onClick={addItem}>
            <AddIcon style={{ fontSize: 16 }} /> Add box
          </OutlineBtn>
        </>
      )}

      {/* Matrix / table */}
      {isMatrix && (
        <>
          <Two>
            <Field>
              <Lbl>Rows</Lbl>
              <Input value={field.rows ?? ''} onChange={e => patchMatrix({ rows: e.target.value })} />
            </Field>
            <Field>
              <Lbl>Columns</Lbl>
              <Input value={field.columns ?? ''} onChange={e => patchMatrix({ columns: e.target.value })} />
            </Field>
          </Two>
          {/* Top-left cell of the grid — the rest of the cells are derived from it. */}
          {coordRow(
            field.matrixLeft,
            field.matrixTop,
            v => patchMatrix({ matrixLeft: v }),
            v => patchMatrix({ matrixTop: v }),
          )}
          <Field>
            <TrackHead>
              <Lbl>Column widths (mm)</Lbl>
              <LinkBtn type="button" onClick={distributeColumns}>
                Distribute evenly
              </LinkBtn>
            </TrackHead>
            <TrackGrid>
              {columnWidthsOf(field).map((w, i) => (
                // Index IS the identity here — a column has nothing else to be
                // keyed by, and reordering columns is not a thing you can do.
                // eslint-disable-next-line react/no-array-index-key
                <TrackCell key={`col${i}`}>
                  <TrackNum>{i + 1}</TrackNum>
                  <Input
                    value={w}
                    inputMode="decimal"
                    onChange={e => setColumnWidth(i, e.target.value)}
                  />
                </TrackCell>
              ))}
            </TrackGrid>
          </Field>
          <Two>
            <Field>
              <Lbl>Row height (mm)</Lbl>
              <Input
                value={rowHeightsOf(field)[0] ?? ''}
                inputMode="decimal"
                onChange={e => setRowHeight(e.target.value)}
              />
            </Field>
            <Field>
              <Lbl>Max characters</Lbl>
              <Input
                value={field.max ?? ''}
                inputMode="numeric"
                placeholder="auto"
                onChange={e => patchField({ max: e.target.value })}
              />
            </Field>
          </Two>
          <Hint>
            Leave Max characters blank to let each cell hold whatever still fits
            its column once the text has shrunk as far as it can.
          </Hint>
          <Hint>
            Drag a column or row line on the form to resize just that one — the
            quickest way to match a printed table. Drag the table to move it, or
            its corner to scale the whole grid.
          </Hint>
        </>
      )}
    </Card>
  );
};

export default FieldEditorCard;

const Card = styled.div<{ $selected: boolean }>`
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding: 14px;
  border: 1px solid ${({ $selected }) => ($selected ? 'var(--primary-600)' : 'var(--border)')};
  border-radius: 10px;
  background: var(--neutral-0);
  box-shadow: ${({ $selected }) => ($selected ? 'var(--shadow-focus)' : 'var(--shadow-xs)')};
  box-sizing: border-box;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const HeaderLeft = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  min-width: 0;
`;

const CardTitle = styled.span`
  font-size: 0.8125rem;
  font-weight: 700;
  color: var(--neutral-800);
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const KindBadge = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--primary-700);
  background: var(--primary-50);
  border: 1px solid var(--primary-100);
  border-radius: 999px;
  padding: 3px 10px;
  flex: 0 0 auto;
`;

const Field = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const Two = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 10px;
`;

const Lbl = styled.label`
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--neutral-600);
`;

const Input = styled.input`
  height: 36px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: var(--mainText);
  box-sizing: border-box;
  width: 100%;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const RenameRow = styled.div`
  display: flex;
  gap: 8px;
`;

const Select = styled.select<{ $invalid?: boolean }>`
  height: 36px;
  padding: 0 10px;
  border: 1px solid
    ${({ $invalid }) => ($invalid ? 'var(--danger-500)' : 'var(--border)')};
  border-radius: 8px;
  background: var(--neutral-0);
  font-size: 0.8125rem;
  color: ${({ $invalid }) => ($invalid ? 'var(--danger-500)' : 'var(--mainText)')};
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: var(--primary-600);
    box-shadow: var(--shadow-focus);
  }
`;

const TrackHead = styled.div`
  display: flex;
  align-items: baseline;
  justify-content: space-between;
  gap: 8px;
`;

const LinkBtn = styled.button`
  border: none;
  background: none;
  padding: 0;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--primary-700);
  cursor: pointer;
  text-decoration: underline;
  text-underline-offset: 2px;
`;

const TrackGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(64px, 1fr));
  gap: 6px;
`;

const TrackCell = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
  min-width: 0;
`;

const TrackNum = styled.span`
  font-size: 0.6875rem;
  font-weight: 700;
  color: var(--neutral-500);
`;

const Err = styled.div`
  color: var(--danger-500, #d44b3f);
  font-size: 0.75rem;
`;

const Hint = styled.div`
  font-size: 0.75rem;
  color: var(--neutral-500);
`;

const DangerHint = styled(Hint)`
  color: var(--danger-500);
`;

const CheckRow = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 0.8125rem;
  color: var(--neutral-700);
  cursor: pointer;
`;

// Segmented pill, the WikiTabs pattern scaled to the editor card's 36 px rows.
const Segmented = styled.div`
  display: flex;
  gap: 4px;
  padding: 3px;
  background: var(--neutral-100);
  border: 1px solid var(--border);
  border-radius: 10px;
  box-sizing: border-box;
`;

const SegmentBtn = styled.button<{ $active: boolean }>`
  flex: 1;
  height: 30px;
  border: none;
  border-radius: 7px;
  font-family: inherit;
  font-weight: 600;
  font-size: 0.8125rem;
  text-transform: none;
  cursor: pointer;
  background: ${({ $active }) => ($active ? 'var(--primary-600)' : 'transparent')};
  color: ${({ $active }) => ($active ? 'var(--neutral-0)' : 'var(--neutral-600)')};
  transition: background-color 0.15s ease, color 0.15s ease;

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const OutcomeRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
`;

// Amber when picked — amber is the colour that means "required" across the
// builder and the inspector's form (RequiredMark, FieldChip).
const OutcomeChip = styled.button<{ $on: boolean }>`
  height: 28px;
  padding: 0 10px;
  border-radius: 999px;
  font-family: inherit;
  font-size: 0.75rem;
  font-weight: 600;
  cursor: pointer;
  border: 1px solid ${({ $on }) => ($on ? 'var(--warning-500, #e0a012)' : 'var(--border)')};
  background: ${({ $on }) => ($on ? 'rgba(224, 160, 18, 0.16)' : 'var(--neutral-0)')};
  color: ${({ $on }) => ($on ? 'var(--neutral-800)' : 'var(--neutral-600)')};
  transition: background-color 0.15s ease, border-color 0.15s ease;

  &:hover:not(:disabled) {
    border-color: ${({ $on }) => ($on ? 'var(--warning-500, #e0a012)' : 'var(--neutral-400)')};
  }

  &:disabled {
    cursor: default;
  }

  &:focus-visible {
    outline: none;
    box-shadow: var(--shadow-focus);
  }
`;

const ItemCard = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-50);
`;

const ItemHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.75rem;
  font-weight: 700;
  color: var(--neutral-600);
`;

const IconBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  border: 1px solid var(--border);
  border-radius: 6px;
  background: var(--neutral-0);
  color: var(--neutral-500);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease, border-color 0.15s ease;

  &:hover {
    background: var(--danger-50, #fdecea);
    border-color: var(--danger-500, #d44b3f);
    color: var(--danger-500, #d44b3f);
  }
`;

const OutlineBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  height: 36px;
  padding: 0 14px;
  border: 1px solid var(--border);
  border-radius: 8px;
  background: var(--neutral-0);
  color: var(--neutral-700);
  font-weight: 600;
  font-size: 0.8125rem;
  cursor: pointer;

  &:hover {
    background: var(--primary-50);
    border-color: var(--primary-600);
    color: var(--primary-700);
  }
`;
